<template>
  <div class="home-page">
    <PullRefresh
      v-model="refreshing"
      :pulling-text="$t('loading')"
      :loosing-text="$t('loading')"
      :loading-text="$t('loading')"
      success-text=""
      :success-duration="0"
      @refresh="onRefresh"
    >
      <main class="home-page__content">
        <header class="home-header">
          <div class="home-header__copy">
            <h1>{{ $t('indexPage.greeting') }}</h1>
            <p>{{ $t('indexPage.subtitle') }}</p>
          </div>
          <img class="home-header__avatar" src="@/assets/img/home/genggo-head.png" alt="">
        </header>

        <section class="home-banner" :aria-label="$t('indexPage.bannerAriaLabel')">
          <ProductPageState
            v-if="bannerLoading"
            class="home-banner__state"
            type="loading"
            compact
          />
          <ProductPageState
            v-else-if="bannerError"
            class="home-banner__state"
            type="error"
            :title="$t('indexPage.bannerLoadFailed')"
            compact
          />
          <ProductPageState
            v-else-if="swipeList.length === 0"
            class="home-banner__state"
            type="empty"
            :title="$t('indexPage.bannerEmptyTitle')"
            :description="$t('indexPage.bannerEmptyDescription')"
          />
          <Swipe
            v-else
            class="home-banner__swipe"
            :autoplay="swipeList.length > 1 ? 3000 : 0"
            :show-indicators="false"
            @change="currentBannerIndex = $event"
          >
            <SwipeItem
              v-for="item in swipeList"
              :key="item.id"
              class="home-banner__slide"
            >
              <div
                class="home-banner__button"
                :class="{ 'is-clickable': isBannerClickable(item) }"
                :aria-disabled="!isBannerClickable(item)"
              >
                <img class="home-banner__image" v-lazy="item.imgUrl" :alt="item.name || ''" @click.stop="onBannerClick(item)">
              </div>
            </SwipeItem>
            <template v-if="swipeList.length > 1" #indicator>
              <div class="home-banner__indicators" aria-hidden="true">
                <span
                  v-for="(item, index) in swipeList"
                  :key="item.id || index"
                  :class="{ 'is-active': currentBannerIndex === index }"
                ></span>
              </div>
            </template>
          </Swipe>
        </section>

        <nav class="home-shortcuts" :aria-label="$t('indexPage.shortcutsAriaLabel')">
          <button
            v-for="item in shortcuts"
            :key="item.key"
            class="home-shortcuts__item"
            type="button"
            @click="goShortcut(item.path)"
          >
            <span class="home-shortcuts__icon" :class="`home-shortcuts__icon--${item.key}`">
              <img :src="item.icon" alt="">
            </span>
            <span class="home-shortcuts__label">{{ $t(item.labelKey) }}</span>
          </button>
        </nav>

        <section class="featured-products">
          <div class="featured-products__heading">
            <h2>{{ $t('indexPage.featuredProducts') }}</h2>
            <button type="button" @click="goProducts">
              {{ $t('indexPage.seeAll') }}
              <Icon name="arrow" />
            </button>
          </div>

          <ProductPageState
            v-if="goodsInitialLoading"
            type="loading"
            compact
          />
          <ProductPageState
            v-else-if="goodsError && goodsList.length === 0"
            type="error"
            compact
          />
          <ProductPageState
            v-else-if="goodsList.length === 0"
            type="empty"
          />
          <template v-else>
            <div ref="featuredProductsGrid" class="featured-products__grid">
              <HomeProductMiniCard
                v-for="(item, index) in goodsList"
                :key="`${item.id}-${index}`"
                class="featured-products__card"
                :product="item"
                :show-rental-schemes="false"
                @click="openProduct"
                @image-load="scheduleMasonryLayout"
              />
            </div>
            <ProductPageState v-if="goodsLoading" class="featured-products__loading" type="loading" compact />
          </template>
        </section>

        <DeliveryBenefitBanner class="home-page__benefits" compact />
      </main>
    </PullRefresh>
    <AppBottomNav />
    <CustomerSupportFloating />
  </div>
</template>

<script>
import { Icon, PullRefresh, Swipe, SwipeItem } from 'vant';
import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import { getGoodsCategory } from '@/api/goods';
import { LIST_TYPE } from '@/common/constants';
import {
  getProductDetailQuery,
  getProductPaginationHasMore,
  normalizeProducts,
  resolveAssetUrl,
} from '@/common/product';
import AppBottomNav from '@/components/AppBottomNav/index.vue';
import CustomerSupportFloating from '@/components/CustomerSupportFloating/index.vue';
import DeliveryBenefitBanner from '@/components/DeliveryBenefitBanner/index.vue';
import HomeProductMiniCard from '@/components/HomeProductMiniCard/index.vue';
import ProductPageState from '@/components/ProductPageState/index.vue';

export default {
  name: 'HomePage',
  components: {
    AppBottomNav,
    CustomerSupportFloating,
    DeliveryBenefitBanner,
    HomeProductMiniCard,
    Icon,
    ProductPageState,
    PullRefresh,
    Swipe,
    SwipeItem,
  },
  data() {
    return {
      bannerLoading: true,
      bannerError: false,
      currentBannerIndex: 0,
      swipeList: [],
      goodsInitialLoading: true,
      goodsLoading: false,
      goodsRequestPromise: null,
      goodsError: false,
      goodsHasMore: true,
      goodsList: [],
      pageNum: 1,
      pageSize: 20,
      refreshing: false,
      bannerRequestPromise: null,
      savedScrollTop: 0,
      scrollListenerActive: false,
      masonry: null,
      masonryImageWatcher: null,
      masonryFrameId: null,
      masonryScheduleVersion: 0,
    };
  },
  computed: {
    shortcuts() {
      return [
        {
          key: 'profile',
          path: '/personal-additional-info',
          icon: require('@/assets/img/home/my-profile.svg'),
          labelKey: 'indexPage.myProfile',
        },
        {
          key: 'products',
          path: '/goods/list',
          icon: require('@/assets/img/home/products.svg'),
          labelKey: 'indexPage.products',
        },
        {
          key: 'stores',
          path: '/stores',
          icon: require('@/assets/img/home/stores.svg'),
          labelKey: 'indexPage.stores',
        },
        {
          key: 'faq',
          path: '/faq',
          icon: require('@/assets/img/home/faq.svg'),
          labelKey: 'indexPage.faq',
        },
      ];
    },
  },
  mounted() {
    this.loadInitialData();
  },
  activated() {
    this.addScrollListener();
    this.scheduleMasonryLayout({ reloadItems: true });
    this.$nextTick(() => {
      const restoreScroll = () => window.scrollTo(0, this.savedScrollTop);
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(restoreScroll);
      } else {
        setTimeout(restoreScroll, 0);
      }
    });
  },
  deactivated() {
    this.removeScrollListener();
  },
  beforeRouteLeave(to, from, next) {
    this.savedScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    next();
  },
  beforeDestroy() {
    this.removeScrollListener();
    this.destroyMasonry();
  },
  methods: {
    async loadInitialData() {
      await Promise.allSettled([
        this.loadBanners(),
        this.loadGoodsPage({ reset: true }),
      ]);
    },
    async loadBanners({ preserveExisting = false } = {}) {
      if (this.bannerRequestPromise) {
        await this.bannerRequestPromise.catch(() => {});
      }
      const requestPromise = this.requestBanners({ preserveExisting });
      this.bannerRequestPromise = requestPromise;
      try {
        return await requestPromise;
      } finally {
        if (this.bannerRequestPromise === requestPromise) {
          this.bannerRequestPromise = null;
        }
      }
    },
    async requestBanners({ preserveExisting = false } = {}) {
      if (!preserveExisting || this.swipeList.length === 0) {
        this.bannerLoading = true;
      }
      try {
        const res = await getGoodsCategory({
          pageNum: 1,
          pageSize: 20,
          location: LIST_TYPE.INDEX_BANNER,
          showPrice: false,
        });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('systemError'));
        }
        const records = Array.isArray(res?.data?.records) ? res.data.records : [];
        this.swipeList = records.map(item => ({
          ...item,
          imgUrl: resolveAssetUrl(item?.imgUrl),
        }));
        this.currentBannerIndex = 0;
        this.bannerError = false;
      } catch (error) {
        if (this.swipeList.length === 0) {
          this.bannerError = true;
        }
        this.$toast(error?.message || this.$t('systemError'));
        throw error;
      } finally {
        this.bannerLoading = false;
      }
    },
    async loadGoodsPage({ reset = false, preserveExisting = false } = {}) {
      if (this.goodsRequestPromise) {
        if (!reset) return this.goodsRequestPromise;
        await this.goodsRequestPromise.catch(() => {});
      }
      const requestPromise = this.requestGoodsPage({ reset, preserveExisting });
      this.goodsRequestPromise = requestPromise;
      try {
        return await requestPromise;
      } finally {
        if (this.goodsRequestPromise === requestPromise) {
          this.goodsRequestPromise = null;
        }
      }
    },
    async requestGoodsPage({ reset = false, preserveExisting = false } = {}) {
      const requestPage = reset ? 1 : this.pageNum;
      this.goodsLoading = true;
      if (reset && (!preserveExisting || this.goodsList.length === 0)) {
        this.goodsInitialLoading = true;
      }
      try {
        const res = await getGoodsCategory({
          pageNum: requestPage,
          pageSize: this.pageSize,
          location: LIST_TYPE.INDEX_GRID,
          showPrice: true,
        });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('systemError'));
        }
        const records = normalizeProducts(res?.data?.records || []);
        const nextList = reset ? records : [...this.goodsList, ...records];
        this.goodsList = nextList;
        this.scheduleMasonryLayout({ reloadItems: true });
        this.goodsHasMore = getProductPaginationHasMore({
          total: res?.data?.total,
          loadedCount: nextList.length,
          receivedCount: records.length,
          pageSize: this.pageSize,
        });
        this.pageNum = requestPage + 1;
        this.goodsError = false;
      } catch (error) {
        if (this.goodsList.length === 0) {
          this.goodsError = true;
          this.goodsHasMore = false;
        }
        this.$toast(error?.message || this.$t('systemError'));
        throw error;
      } finally {
        this.goodsLoading = false;
        this.goodsInitialLoading = false;
      }
    },
    loadMoreGoods() {
      if (!this.goodsHasMore || this.goodsInitialLoading) {
        this.goodsLoading = false;
        return;
      }
      this.loadGoodsPage().catch(() => {
        // Keep hasMore for the next natural scroll retry.
      });
    },
    handleHomeScroll() {
      if (!this.goodsHasMore || this.goodsLoading || this.goodsInitialLoading || this.goodsList.length === 0) {
        return;
      }
      const documentElement = document.documentElement;
      const scrollTop = window.pageYOffset || documentElement.scrollTop || 0;
      const viewportHeight = window.innerHeight || documentElement.clientHeight || 0;
      const distanceToBottom = documentElement.scrollHeight - scrollTop - viewportHeight;
      if (distanceToBottom <= 240) {
        this.loadMoreGoods();
      }
    },
    addScrollListener() {
      if (this.scrollListenerActive) return;
      window.addEventListener('scroll', this.handleHomeScroll, { passive: true });
      this.scrollListenerActive = true;
    },
    removeScrollListener() {
      if (!this.scrollListenerActive) return;
      window.removeEventListener('scroll', this.handleHomeScroll);
      this.scrollListenerActive = false;
    },
    getMasonryGutter(grid) {
      const gutter = window.getComputedStyle(grid).columnGap;
      return Number.parseFloat(gutter) || 0;
    },
    scheduleMasonryLayout({ reloadItems = false, watchImages = false } = {}) {
      const scheduleVersion = this.masonryScheduleVersion;
      this.$nextTick(() => {
        if (this._isBeingDestroyed || this._isDestroyed || scheduleVersion !== this.masonryScheduleVersion) {
          return;
        }
        if (this.masonryFrameId !== null) {
          window.cancelAnimationFrame(this.masonryFrameId);
        }
        this.masonryFrameId = window.requestAnimationFrame(() => {
          this.masonryFrameId = null;
          if (this._isBeingDestroyed || this._isDestroyed || scheduleVersion !== this.masonryScheduleVersion) {
            return;
          }
          const grid = this.$refs.featuredProductsGrid;
          if (!grid) return;
          if (!this.masonry) {
            this.masonry = new Masonry(grid, {
              itemSelector: '.featured-products__card',
              columnWidth: '.featured-products__card',
              gutter: this.getMasonryGutter(grid),
              percentPosition: true,
              transitionDuration: 0,
            });
            watchImages = true;
          } else if (reloadItems) {
            this.masonry.reloadItems();
            watchImages = true;
          }
          this.masonry.options.gutter = this.getMasonryGutter(grid);
          this.masonry.layout();
          if (watchImages) {
            this.watchMasonryImages(grid);
          }
        });
      });
    },
    watchMasonryImages(grid) {
      if (this.masonryImageWatcher) {
        this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
        this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
      }
      this.masonryImageWatcher = imagesLoaded(grid);
      this.masonryImageWatcher.on('progress', this.scheduleMasonryLayout);
      this.masonryImageWatcher.on('always', this.scheduleMasonryLayout);
    },
    destroyMasonry() {
      this.masonryScheduleVersion += 1;
      if (this.masonryFrameId !== null) {
        window.cancelAnimationFrame(this.masonryFrameId);
        this.masonryFrameId = null;
      }
      if (this.masonryImageWatcher) {
        this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
        this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
        this.masonryImageWatcher = null;
      }
      if (this.masonry) {
        this.masonry.destroy();
        this.masonry = null;
      }
    },
    async onRefresh() {
      await Promise.allSettled([
        this.loadBanners({ preserveExisting: true }),
        this.loadGoodsPage({ reset: true, preserveExisting: true }),
      ]);
      this.refreshing = false;
    },
    onBannerClick(item) {
      if (!this.isBannerClickable(item)) return;
      const goodsId = item?.goodsId === null || item?.goodsId === undefined
        ? ''
        : String(item.goodsId).trim();
      if (!goodsId) return;
      this.$router.push({
        name: 'GoodsDetail',
        query: { goodsId },
      });
    },
    isBannerClickable(item) {
      if (item?.targetType !== 'GOODS_DETAIL') return false;
      return item?.goodsId !== null && item?.goodsId !== undefined && String(item.goodsId).trim() !== '';
    },
    openProduct(product) {
      const query = getProductDetailQuery(product);
      if (!query) return;
      this.$router.push({ name: 'GoodsDetail', query });
    },
    goProducts() {
      this.$router.push('/goods/list');
    },
    goShortcut(path) {
      this.$router.push(path);
    },
  },
};
</script>

<style lang="less" scoped>
.home-page {
  min-height: 100vh;
  background: #F4F8FF;
  color: #070D3F;
}

.home-page__content {
  box-sizing: border-box;
  width: 100%;
  max-width: 880px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 28px 16px calc(164px + env(safe-area-inset-bottom));
}

.home-header {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.home-header__copy {
  min-width: 0;

  h1,
  p {
    margin: 0;
  }

  h1 {
    overflow: hidden;
    font-size: 36px;
    font-weight: 600;
    line-height: 52px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    overflow: hidden;
    color: #6B7280;
    font-size: 26px;
    line-height: 36px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.home-header__avatar {
  display: block;
  width: 88px;
  height: 88px;
  flex: 0 0 auto;
  border-radius: 50%;
}

.home-banner {
  height: 284px;
  margin-top: 16px;
  overflow: hidden;
  border-radius: 20px;
}

.home-banner__state,
.home-banner__swipe,
.home-banner__slide,
.home-banner__button,
.home-banner__image {
  width: 100%;
  height: 100%;
}

.home-banner__state {
  box-sizing: border-box;
}

.home-banner__button {
  display: block;
  padding: 0;
  overflow: hidden;
  background: #FFFFFF;
  touch-action: pan-y;

  &.is-clickable {
    cursor: pointer;
  }
}

.home-banner__image {
  display: block;
  object-fit: fill;
  -webkit-user-drag: none;
  user-select: none;
}

.home-banner__swipe {
  touch-action: pan-y;
}

.home-banner__swipe ::v-deep .van-swipe__track,
.home-banner__swipe ::v-deep .van-swipe-item {
  touch-action: pan-y;
}

.home-banner__indicators {
  position: absolute;
  right: 0;
  bottom: 16px;
  left: 0;
  display: flex;
  height: 12px;
  align-items: center;
  justify-content: center;
  gap: 8px;

  span {
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.58);
    border-radius: 999px;
    transition: width 0.2s ease, background-color 0.2s ease;

    &.is-active {
      width: 40px;
      background: #FFFFFF;
    }
  }
}

.home-shortcuts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;
  padding: 16px 8px 18px;
  background: #FFFFFF;
  border: 2px solid #E5EAF3;
  border-radius: 20px;
}

.home-shortcuts__item {
  display: flex;
  min-width: 0;
  padding: 0;
  align-items: center;
  flex-direction: column;
  background: transparent;
  border: 0;
  color: #070D3F;
  font-family: inherit;
  cursor: pointer;
}

.home-shortcuts__icon {
  display: flex;
  width: 116px;
  height: 116px;
  align-items: center;
  justify-content: center;
  border-radius: 20px;

  &--profile {
    background: #EEF4FF;
  }

  &--products {
    background: #EAF8F1;
  }

  &--stores {
    background: #FFF4EE;
  }

  &--faq {
    background: #EAF9FC;
  }

  img {
    display: block;
    width: 64px;
    height: 64px;
  }

  &--products img {
    width: 80px;
    height: 80px;
  }
}

.home-shortcuts__label {
  display: -webkit-box;
  width: 100%;
  min-height: 32px;
  margin-top: 14px;
  overflow: hidden;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  text-align: center;
  word-break: normal;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.featured-products {
  margin-top: 24px;
}

.featured-products__heading {
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h2 {
    min-width: 0;
    margin: 0;
    overflow: hidden;
    font-size: 32px;
    font-weight: 600;
    line-height: 48px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    display: flex;
    padding: 0;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: 0;
    color: #0B45FF;
    font-family: inherit;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    cursor: pointer;
  }

  .van-icon {
    font-size: 26px;
  }
}

.featured-products__grid {
  --featured-products-column-gap: 16px;
  position: relative;
  width: 100%;
  column-gap: var(--featured-products-column-gap);
}

::v-deep .featured-products__card {
  display: block;
  width: calc((100% - var(--featured-products-column-gap)) / 2);
  margin: 0 0 var(--featured-products-column-gap);
}

::v-deep .featured-products__card.product-mini-card {
  height: auto;
}

.home-page__benefits {
  margin-top: 16px;
}

::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}

@media (max-width: 340px) {
  .home-page__content {
    padding-right: 12px;
    padding-left: 12px;
  }

  .home-shortcuts {
    gap: 4px;
  }

  .home-shortcuts__icon {
    width: 104px;
    height: 104px;
  }

  .featured-products__grid {
    --featured-products-column-gap: 8px;
  }

  ::v-deep .featured-products__card {
    margin-bottom: var(--featured-products-column-gap);
  }
}
</style>
